import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {ECardThemesIds} from '@/dataSource/src/api/cards-theme/entities';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';

import {ONBOARDING_TEEN_FIRST_NAME, ONBOARDING_TEEN_LAST_NAME} from '../models';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  selectedTheme: ECardThemesIds | undefined;
  firstName: string;
  lastName: string;
  isDisabled: boolean;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  handleContinue: () => Promise<void>;
  handleOnClickChangeDesign: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  const {updateMetadata, profile} = useUsersRepository();
  const location = useLocation();
  const {selectedTheme} = location.state || {};
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const navigate = useNavigate();
  const {
    trackSelectOnboardingChangeCardTheme,
    trackSelectOnboardingKidNameContinue,
    trackViewOnboardingKidName,
  } = useTrackers();

  const handleContinue = useCallback(async () => {
    trackSelectOnboardingKidNameContinue(selectedTheme, profile?.id);

    await updateMetadata({
      ONBOARDING_CARD_THEME_SELECTED: selectedTheme,
    });
    localStorage.setItem(ONBOARDING_TEEN_FIRST_NAME, firstName);
    localStorage.setItem(ONBOARDING_TEEN_LAST_NAME, lastName);

    navigate('/onboarding/journey-child');
  }, [
    firstName,
    lastName,
    navigate,
    profile?.id,
    selectedTheme,
    trackSelectOnboardingKidNameContinue,
    updateMetadata,
  ]);

  useEffect(() => {
    if (firstName)
      setFirstName(firstName.charAt(0).toUpperCase() + firstName.slice(1));
  }, [firstName]);

  useEffect(() => {
    if (lastName)
      setLastName(lastName.charAt(0).toUpperCase() + lastName.slice(1));
  }, [lastName]);

  useEffect(() => {
    trackViewOnboardingKidName(selectedTheme, profile?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, selectedTheme]);

  const isDisabled = useMemo(
    () => !firstName || !lastName,
    [firstName, lastName],
  );

  const handleOnClickChangeDesign = useCallback(() => {
    trackSelectOnboardingChangeCardTheme(selectedTheme, profile?.id);
    navigate('/onboarding/choose-card-theme-category');
  }, [
    navigate,
    profile?.id,
    selectedTheme,
    trackSelectOnboardingChangeCardTheme,
  ]);

  return (
    <ViewModelContext.Provider
      value={{
        selectedTheme,
        isDisabled,
        firstName,
        lastName,
        setFirstName,
        setLastName,
        handleContinue,
        handleOnClickChangeDesign,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('TeenPreloadCardData ViewModelContext is undefined');
  }
  return context;
}
